import { useEffect } from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type { FetchOptions } from './fetchResponse.ts';
import { useFetchData } from './useFetchData.ts';
import type { TransformErrorType } from './handleError.ts';
import {
	type QueryOutcome,
	useExperimentalQueryExecution,
} from './useExperimentalQueryExecution.ts';
import { INITIAL_IDLE_STATE, INITIAL_LOADING_STATE } from './useExperimentalQueryState.ts';
import { generateCacheKey } from './generateCacheKey.ts';

export type QueryResultWithRefetch<TData, TVariables = any> = {
	data: TData | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	called: boolean;
	refetch: (variables?: TVariables) => Promise<QueryOutcome<TData>>;
	stopPolling: () => void;
};

export type QueryResult<TData> = {
	data: TData | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	called: boolean;
	stopPolling?: () => void;
};

export type FetchPolicy = 'cache-first' | 'network-only' | 'cache-and-network' | 'no-cache';

export type QueryCallbackOptions<TData> = {
	onCompleted?: (data: TData) => void;
	onError?: (error: ApolloErrorInterop) => void;
	transformError?: TransformErrorType;
};

export type QueryExecutionOptions = {
	fetchPolicy?: FetchPolicy;
	pollInterval?: number;
};

export type UseExperimentalQueryParams<TData, TVariables, TResponse> = FetchOptions<
	TVariables,
	TResponse
> &
	QueryCallbackOptions<TData> &
	QueryExecutionOptions & {
		variables?: TVariables;
		parseResult: (response: TResponse, variables: TVariables) => TData | Promise<TData>;
		skip?: boolean;
	};

export function useExperimentalQuery<TData, TVariables, TResponse>({
	url,
	method,
	headers,
	body,
	variables,
	credentials,
	parseResult,
	parseResponseOnSuccess,
	parseResponseOnError,
	onCompleted,
	onError,
	transformError,
	skip = false,
	fetchPolicy = 'cache-first',
	pollInterval,
}: UseExperimentalQueryParams<TData, TVariables, TResponse>): QueryResultWithRefetch<
	TData,
	TVariables
> {
	const { fetchData } = useFetchData<TData, TVariables, TResponse>({
		url,
		method,
		headers,
		body,
		credentials,
		variables,
		parseResult,
		parseResponseOnSuccess,
		parseResponseOnError,
	});

	const key = generateCacheKey({
		url,
		method,
		headers,
		body,
		credentials,
		variables,
	});

	return useExperimentalQueryBase<TData, TVariables>({
		key,
		fetchData,
		variables,
		onCompleted,
		onError,
		transformError,
		skip,
		fetchPolicy,
		pollInterval,
	});
}

type UseExperimentalQueryBaseParams<TData, TVariables> = QueryCallbackOptions<TData> &
	QueryExecutionOptions & {
		key: string;
		fetchData: (variables?: TVariables) => TData | Promise<TData>;
		variables?: TVariables;
		skip?: boolean;
	};

export function useExperimentalQueryBase<TData, TVariables>({
	key,
	fetchData,
	onCompleted,
	onError,
	transformError,
	skip = false,
	fetchPolicy = 'cache-first',
	pollInterval,
}: UseExperimentalQueryBaseParams<TData, TVariables>): QueryResultWithRefetch<TData, TVariables> {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR);

	const { queryState, execute, refetch, stopPolling } = useExperimentalQueryExecution<
		TData,
		TVariables
	>({
		key,
		fetchData,
		onCompleted,
		onError,
		transformError,
		fetchPolicy,
		isLazyQuery: false,
		pollInterval,
		isSSR,
	});

	useEffect(() => {
		// Edit Page uses JSDOM, so effects run in SSR. Experimental queries should not execute during SSR
		if (!skip && !isSSR) {
			void execute();
		}
	}, [execute, key, skip, fetchPolicy, isSSR]);

	// If skip is true, return idle state with refetch
	if (skip) {
		return { ...INITIAL_IDLE_STATE, refetch, stopPolling };
	}

	// Use store state if available, otherwise return initial loading state with refetch
	// (stable reference prevents extra renders before query executes)
	return queryState
		? { ...queryState, refetch, stopPolling }
		: { ...INITIAL_LOADING_STATE, refetch, stopPolling };
}
